import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';

/**
 * AdminDashboard Component
 *
 * A dashboard for administrators to access administrative functions and system management.
 *
 * Features:
 * - Question Review Management: Access to flagged questions review system
 * - System Administration: Future admin tools and system management features
 * - Access Control: Only accessible to users with admin role
 *
 * The component includes authorization checks to ensure only admins can access the dashboard.
 */
const AdminDashboard: React.FC = () => {
  const { currentUser } = useUser();
  const navigate = useNavigate();

  // Check if user has admin permission
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto py-8 px-4">
          <div className="card-main border-red-200 bg-red-50">
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-red-900 mb-2">Access Denied</h3>
              <p className="text-red-700">
                You need admin permissions to access the admin dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6">
        <h1 className="text-section-heading">Admin Dashboard</h1>
        <p className="text-gray-600 mt-2">System administration and management tools</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Question Review Management Card */}
        <div className="card-main hover:shadow-lg transition-shadow">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-content-title font-semibold">Question Reviews</h3>
                <p className="text-sm text-gray-600">Manage flagged questions</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Review and manage questions that have been flagged by students for accuracy issues.
              Update answers and recalculate scores as needed.
            </p>
            <button
              onClick={() => navigate('/admin/questions')}
              className="w-full px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors text-sm font-medium"
            >
              Review Flagged Questions
            </button>
          </div>
        </div>

        {/* User Management Card - Future Feature */}
        <div className="card-main opacity-50">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-2.239" />
                </svg>
              </div>
              <div>
                <h3 className="text-content-title font-semibold">User Management</h3>
                <p className="text-sm text-gray-600">Manage users and roles</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Manage user accounts, assign roles, and handle user-related administrative tasks.
            </p>
            <button
              disabled
              className="w-full px-4 py-2 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed text-sm font-medium"
            >
              Coming Soon
            </button>
          </div>
        </div>

        {/* System Settings Card - Future Feature */}
        <div className="card-main opacity-50">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-content-title font-semibold">System Settings</h3>
                <p className="text-sm text-gray-600">Configure system settings</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Configure system-wide settings, manage application configuration, and system maintenance.
            </p>
            <button
              disabled
              className="w-full px-4 py-2 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed text-sm font-medium"
            >
              Coming Soon
            </button>
          </div>
        </div>

        {/* Analytics Card - Future Feature */}
        <div className="card-main opacity-50">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v4a2 2 0 01-2 2h-2a2 2 0 00-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-content-title font-semibold">Analytics</h3>
                <p className="text-sm text-gray-600">System analytics and reports</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              View system analytics, user engagement metrics, and generate administrative reports.
            </p>
            <button
              disabled
              className="w-full px-4 py-2 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed text-sm font-medium"
            >
              Coming Soon
            </button>
          </div>
        </div>

        {/* Question Management Card - Future Feature */}
        <div className="card-main opacity-50">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-content-title font-semibold">Question Bank</h3>
                <p className="text-sm text-gray-600">Manage questions and content</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Add, edit, and organize questions in the question bank. Manage content and syllabus alignment.
            </p>
            <button
              disabled
              className="w-full px-4 py-2 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed text-sm font-medium"
            >
              Coming Soon
            </button>
          </div>
        </div>

        {/* System Health Card - Future Feature */}
        <div className="card-main opacity-50">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <h3 className="text-content-title font-semibold">System Health</h3>
                <p className="text-sm text-gray-600">Monitor system status</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Monitor system health, check database connectivity, and view system performance metrics.
            </p>
            <button
              disabled
              className="w-full px-4 py-2 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed text-sm font-medium"
            >
              Coming Soon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;